import { useEffect, useRef, useState } from 'react';
import { COLUMN_LABELS, SECTION_LABELS, canSee } from '../lib/permissions';
import { TASK_STATUSES, type Section } from '../lib/types';
import { useStore } from '../store/useStore';

/**
 * The member's own half of the layout.
 *
 * An admin decides what this person starts with; from here they arrange it for
 * themselves — which columns they keep in view, how tight the cards sit, and
 * which page the team opens on. Nothing in here can widen what they may see: the
 * page list is drawn from the access they already have.
 */
export function ViewMenu() {
  const layout = useStore((s) => s.layout);
  const access = useStore((s) => s.access);
  const hasOwnLayout = useStore((s) => s.hasOwnLayout);
  const saveLayout = useStore((s) => s.saveLayout);
  const resetLayout = useStore((s) => s.resetLayout);

  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);

  // Click-away rather than a modal: this is a preference panel, and the board
  // behind it should stay readable while it is open.
  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent): void => {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  function toggleColumn(status: (typeof TASK_STATUSES)[number]): void {
    const on = layout.boardColumns.includes(status);
    const next = on
      ? layout.boardColumns.filter((value) => value !== status)
      : TASK_STATUSES.filter((value) => value === status || layout.boardColumns.includes(value));
    // A board with no columns is indistinguishable from a broken one, so the
    // last one cannot be turned off.
    if (next.length === 0) return;
    void saveLayout({ boardColumns: next });
  }

  const pages = (['board', 'activity', 'releases'] as Section[]).filter((section) =>
    canSee(access, section),
  );

  return (
    <div ref={wrapper} className="relative ml-auto">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="btn btn-ghost !py-1.5 !text-xs"
        aria-expanded={open}
      >
        <svg viewBox="0 0 16 16" className="size-3.5" fill="currentColor" aria-hidden="true">
          <path d="M2.5 4.25A.75.75 0 0 1 3.25 3.5h9.5a.75.75 0 0 1 0 1.5h-9.5a.75.75 0 0 1-.75-.75Zm1.5 3.75A.75.75 0 0 1 4.75 7.25h6.5a.75.75 0 0 1 0 1.5h-6.5A.75.75 0 0 1 4 8Zm1.75 3a.75.75 0 0 0 0 1.5h4.5a.75.75 0 0 0 0-1.5h-4.5Z" />
        </svg>
        View
      </button>

      {open && (
        <div className="animate-rise card absolute right-0 z-30 mt-2 flex w-72 flex-col gap-4 p-4">
          <fieldset className="flex flex-col gap-1.5">
            <legend className="text-xs font-medium text-ink-soft">Columns</legend>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {TASK_STATUSES.map((status) => {
                const on = layout.boardColumns.includes(status);
                return (
                  <button
                    key={status}
                    type="button"
                    onClick={() => toggleColumn(status)}
                    aria-pressed={on}
                    className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors ${
                      on
                        ? 'border-accent bg-accent/15 text-ink'
                        : 'border-line text-ink-soft hover:bg-hover'
                    }`}
                  >
                    {COLUMN_LABELS[status]}
                  </button>
                );
              })}
            </div>
          </fieldset>

          <fieldset className="flex flex-col gap-1.5">
            <legend className="text-xs font-medium text-ink-soft">Card size</legend>
            <div className="mt-1 flex gap-1.5">
              {(['comfortable', 'compact'] as const).map((density) => (
                <button
                  key={density}
                  type="button"
                  onClick={() => void saveLayout({ density })}
                  aria-pressed={layout.density === density}
                  className={`flex-1 rounded-lg border px-2.5 py-1 text-[11px] capitalize transition-colors ${
                    layout.density === density
                      ? 'border-accent bg-accent/15 text-ink'
                      : 'border-line text-ink-soft hover:bg-hover'
                  }`}
                >
                  {density}
                </button>
              ))}
            </div>
          </fieldset>

          {pages.length > 1 && (
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-ink-soft">Open this team on</span>
              <select
                value={layout.landing}
                onChange={(event) => void saveLayout({ landing: event.target.value as Section })}
                className="field-input text-sm"
              >
                {pages.map((section) => (
                  <option key={section} value={section}>
                    {SECTION_LABELS[section].title}
                  </option>
                ))}
              </select>
            </label>
          )}

          {hasOwnLayout && (
            <button
              type="button"
              onClick={() => void resetLayout().catch(() => undefined)}
              className="btn btn-ghost !py-1 !text-xs"
            >
              Back to the team default
            </button>
          )}
        </div>
      )}
    </div>
  );
}
